import { isSportsOrg } from './orgTypes';

const CORE_ROLES = Object.freeze(['player', 'coach', 'staff']);
const ADDITIONAL_BUCKET_ROLES = Object.freeze(['coach_staff', 'unassigned', 'extra']);

const BASE_ROLE_STYLES = {
  player: {
    label: 'Player',
    bg: 'bg-blue-100',
    text: 'text-blue-800',
    border: 'border border-blue-200',
  },
  coach: {
    label: 'Coach',
    bg: 'bg-purple-100',
    text: 'text-purple-800',
    border: 'border border-purple-200',
  },
  staff: {
    label: 'Staff',
    bg: 'bg-pink-100',
    text: 'text-pink-800',
    border: 'border border-pink-200',
  },
  coach_staff: {
    label: 'Coaches + Staff',
    bg: 'bg-purple-100',
    text: 'text-purple-800',
    border: 'border border-purple-200',
  },
  unassigned: {
    label: 'Unassigned',
    bg: 'bg-slate-100',
    text: 'text-slate-700',
    border: 'border border-slate-300',
  },
  extra: {
    label: 'Extras',
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border border-amber-200',
  },
};

const DEFAULT_STYLE = {
  label: 'Member',
  bg: 'bg-muted/60',
  text: 'text-foreground',
  border: 'border border-border',
};

const withBadgeClass = (style) => {
  const { border, bg, text, badge } = style || {};
  const badgeClass = badge || [border, bg, text].filter(Boolean).join(' ').trim();
  return {
    ...style,
    badge: badgeClass,
  };
};

const DEFAULT_STYLE_WITH_BADGE = withBadgeClass(DEFAULT_STYLE);

const ROLE_STYLE_MAP = Object.freeze(
  Object.keys(BASE_ROLE_STYLES).reduce((acc, key) => {
    acc[key] = withBadgeClass(BASE_ROLE_STYLES[key]);
    return acc;
  }, {})
);

export const TEAM_ROLE_KEYS = CORE_ROLES;
export const ROLE_BUCKET_KEYS = Object.freeze([...CORE_ROLES, ...ADDITIONAL_BUCKET_ROLES]);
export const DEFAULT_ROLE_KEY = 'player';

export const getRoleStyle = (role, fallback = DEFAULT_ROLE_KEY) => {
  const key = typeof role === 'string' ? role.trim().toLowerCase() : '';
  if (key && ROLE_STYLE_MAP[key]) {
    return ROLE_STYLE_MAP[key];
  }
  if (fallback && ROLE_STYLE_MAP[fallback]) {
    return ROLE_STYLE_MAP[fallback];
  }
  return DEFAULT_STYLE_WITH_BADGE;
};

export const getRoleBadgeClasses = (role, fallback) => getRoleStyle(role, fallback).badge;

export const getRoleLabel = (role, fallbackLabel = DEFAULT_STYLE_WITH_BADGE.label) =>
  getRoleStyle(role).label || fallbackLabel;

export const ROLE_LABELS = ROLE_BUCKET_KEYS.reduce((acc, key) => {
  acc[key] = ROLE_STYLE_MAP[key]?.label || DEFAULT_STYLE_WITH_BADGE.label;
  return acc;
}, {});

export const ROLE_BADGE_CLASSES = ROLE_BUCKET_KEYS.reduce((acc, key) => {
  acc[key] = ROLE_STYLE_MAP[key]?.badge || DEFAULT_STYLE_WITH_BADGE.badge;
  return acc;
}, {});

export const ROLE_DISPLAY_CONFIG = ROLE_STYLE_MAP;

// ---------------------------------------------------------------------------
// Org-aware role labels.
//
// The stored team_role enum values ('player' | 'coach' | 'staff') are the same
// for every org type — org type ONLY changes the words rendered. Sports orgs
// (college / high_school / club_sports, and any team without an org_type) keep
// the athletics labels above; 'other' orgs see neutral labels.
// ---------------------------------------------------------------------------
const NEUTRAL_ROLE_LABELS = Object.freeze({
  player: { singular: 'Member', plural: 'Members' },
  coach: { singular: 'Organizer', plural: 'Organizers' },
  staff: { singular: 'Staff', plural: 'Staff' },
  coach_staff: { singular: 'Organizers + Staff', plural: 'Organizers + Staff' },
  unassigned: { singular: 'Unassigned', plural: 'Unassigned' },
  extra: { singular: 'Extras', plural: 'Extras' },
});

const SPORTS_PLURAL_LABELS = Object.freeze({
  player: 'Players',
  coach: 'Coaches',
  staff: 'Staff',
  coach_staff: 'Coaches + Staff',
  unassigned: 'Unassigned',
  extra: 'Extras',
});

const normalizeRoleKey = (role) =>
  typeof role === 'string' ? role.trim().toLowerCase() : '';

export const getOrgRoleLabel = (role, orgType, { plural = false } = {}) => {
  const key = normalizeRoleKey(role);
  if (isSportsOrg(orgType)) {
    if (plural) return SPORTS_PLURAL_LABELS[key] || getRoleLabel(role);
    return getRoleLabel(role);
  }
  const neutral = NEUTRAL_ROLE_LABELS[key];
  if (!neutral) return getRoleLabel(role);
  return plural ? neutral.plural : neutral.singular;
};

// Dropdown options for member role pickers, labeled for the org type.
// Values are always the stored enum values.
export const getOrgRoleOptions = (orgType) =>
  CORE_ROLES.map((role) => ({ value: role, label: getOrgRoleLabel(role, orgType) }));

// Translate an already-built display string containing sports role words
// (e.g. "Players, Coaches + Staff") into neutral vocabulary for non-sports
// orgs. Used where a summary string was composed upstream of org context.
export const translateRoleWordsForOrg = (text, orgType) => {
  const input = String(text ?? '');
  if (!input || isSportsOrg(orgType)) return input;
  return input
    .replace(/\bPlayers\b/g, 'Members')
    .replace(/\bPlayer\b/g, 'Member')
    .replace(/\bCoaches\b/g, 'Organizers')
    .replace(/\bCoach\b/g, 'Organizer')
    .replace(/\bplayers\b/g, 'members')
    .replace(/\bplayer\b/g, 'member')
    .replace(/\bcoaches\b/g, 'organizers')
    .replace(/\bcoach\b/g, 'organizer');
};
